import RestClient from './RestClient';
import User from '../models/User';
import { getStore } from './getStore';
import type { FetchOptions } from './Client';

type McpPersonalAccessToken = {
    _id: string;
    user: string;
    name: string;
    token_prefix: string;
    scopes: string[];
    expiresAt: string;
    lastUsedAt?: string | null;
    revokedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type CalendarTokenStatus = {
    has_token: boolean;
    revoked: boolean;
    token_prefix?: string | null;
    last_used_at?: string | null;
    webcal_url?: string | null;
    https_url?: string | null;
    team_webcal_url?: string | null;
    team_https_url?: string | null;
    calendar_token?: {
        _id?: string;
        user?: string;
        token_prefix?: string;
        revokedAt?: string | null;
    } | null;
};

class Users extends RestClient {
    constructor() {
        super({
            model: User,
            entryPoint: 'users',
            sortBy: 'lastname',
            id_field: '_id',
            // redux config
            resource: 'user',
            resources: 'users',
        });
    }

    create(payload: AnyObj, params?: AnyObj) {
        const modelInstance = new User(payload) as User;
        const prepared = 'prepareForUpdate' in modelInstance && typeof modelInstance.prepareForUpdate === 'function'
            ? modelInstance.prepareForUpdate()
            : modelInstance;

        let data: AnyObj = { [this.resource]: prepared };
        if (params)
            data = { ...params, ...data };

        return this.POST(`/${this.entryPoint}${this.CREATE_PATH}`, data)
            .then(response => response.json())
            .then(json => {
                if (json[this.resource]) {
                    const user = new User(json[this.resource]) as User;
                    if (json.token)
                        user.creationToken = json.token;
                    return { ...json, [this.resource]: user };
                }
                throw new Error(json.err || json.error || '');
            });
    }

    addFavoriteProjects(projects: string[]) {
        return this.POST(`/${this.entryPoint}/projects`, projects)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json.user;
                }
                throw new Error(json.flash.error);
            });
    }

    removeFavoriteProjects(projects: string[]) {
        return this.DELETE(`/${this.entryPoint}/projects/${projects.join(',')}`)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json.user;
                }
                throw new Error(json.flash.error);
            });
    }

    setAvatar(id: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);

        const headers: AnyObj = {};
        const subscriberId = getStore().getState()?.mercure?.subscriberId;
        if (subscriberId)
            headers['X-MERCURE-SUBSCRIBER'] = subscriberId;

        let url = `${this.baseUrl}/${this.entryPoint}/${id}/avatar`;
        const options: FetchOptions = {
            method: 'POST',
            body: formData,
            headers,
            credentials: 'include',
        };

        return fetch(url, options)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    unsetAvatar(id: string) {
        return this.DELETE(`/${this.entryPoint}/${id}/avatar`)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    deactivate(id: string) {
        return this.POST(`/${this.entryPoint}/${id}/deactivate`, {})
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not disable user');
            });
    }

    activate(id: string) {
        return this.POST(`/${this.entryPoint}/${id}/activate`, {})
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.err || json.error || 'Could not enable user');
            });
    }

    listMcpPersonalAccessTokens() : Promise<McpPersonalAccessToken[]> {
        return this.GET(`/${this.entryPoint}/mcp/personal_access_tokens`)
            .then(response => response.json())
            .then(json => {
                if (Array.isArray(json.personal_access_tokens))
                    return json.personal_access_tokens;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to list personal access tokens');
            });
    }

    createMcpPersonalAccessToken(payload: { name: string, scopes: string[], expires_at: string }) : Promise<{ token: string, personal_access_token: McpPersonalAccessToken }> {
        return this.POST(`/${this.entryPoint}/mcp/personal_access_tokens`, {
            personal_access_token: payload
        })
            .then(response => response.json())
            .then(json => {
                if (json.token && json.personal_access_token)
                    return json;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to create personal access token');
            });
    }

    revokeMcpPersonalAccessToken(id: string) : Promise<McpPersonalAccessToken> {
        return this.DELETE(`/${this.entryPoint}/mcp/personal_access_tokens/${id}`)
            .then(response => response.json())
            .then(json => {
                if (json.personal_access_token)
                    return json.personal_access_token;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to revoke personal access token');
            });
    }

    getCalendarToken() : Promise<CalendarTokenStatus> {
        return this.GET(`/${this.entryPoint}/calendar_token`)
            .then(response => response.json())
            .then(json => {
                if (json && typeof json.has_token === 'boolean')
                    return json as CalendarTokenStatus;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to load calendar token');
            });
    }

    createCalendarToken() : Promise<{ token: string } & CalendarTokenStatus> {
        return this.POST(`/${this.entryPoint}/calendar_token`, {})
            .then(response => response.json())
            .then(json => {
                if (json?.token)
                    return json;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to create calendar token');
            });
    }

    revokeCalendarToken() : Promise<CalendarTokenStatus> {
        return this.DELETE(`/${this.entryPoint}/calendar_token`)
            .then(response => response.json())
            .then(json => {
                if (json && typeof json.has_token === 'boolean')
                    return json as CalendarTokenStatus;
                throw new Error(json.err || json.error || json.flash?.error || 'Unable to revoke calendar token');
            });
    }
}

const UsersApi = new Users();

export default UsersApi;
